import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DesktopLayout } from '@/components/desktop/DesktopLayout';
import { restaurantService } from '@/services/restaurant';
import type { RestaurantWithProduct } from '@/types';
import { useLocationStore } from '@/store/location';
import { useToast } from '@/components/common/Toast';
import { useFilterStore } from '@/store/filter';
import { favoritesService } from '@/services/favorites';
import { useAuth } from '@/contexts/AuthContext';
import { useStoreFiltering } from '@/hooks/useStoreFiltering';
import { HomeSEO } from '@/components/seo/SEO';
import { SCHEMA_TEMPLATES } from '@/constants/seo';

// Reuse mobile components
import { HomeHeader } from '@/components/home/HomeHeader';
import { OrderTypeSelector } from '@/components/home/OrderTypeSelector';
import { BannerCarousel } from '@/components/home/BannerCarousel';
import { ViewToggle } from '@/components/home/ViewToggle';
import { StoreFilter } from '@/components/home/StoreFilter';
import { StoreListWrapper } from '@/components/desktop/StoreListWrapper';
import { DesktopMapView } from '@/components/desktop/DesktopMapView';
import { Footer } from '@/components/common/Footer';

export function DesktopHomePage() {
  const navigate = useNavigate();
  const { currentLocation, checkAuthAndLoad } = useLocationStore();
  const { showToast, ToastContainer } = useToast();
  const { selectedStoreTypes, showOpenOnly, showClosedOnly } = useFilterStore();
  const { user, isLoading: authLoading } = useAuth();
  
  // Core states - reuse from mobile
  const [isMapView, setIsMapView] = useState(false);
  const [restaurants, setRestaurants] = useState<RestaurantWithProduct[]>([]);
  const [deadlineProductsByStore, setDeadlineProductsByStore] = useState<Map<string, any[]> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDiscountType, setSelectedDiscountType] = useState<'DEADLINE_DISCOUNT' | 'RESERVATION_DISCOUNT' | 'REGULAR_ORDER'>(() => {
    const last = localStorage.getItem('lastHomeOrderTypeDesktop') as 'DEADLINE_DISCOUNT' | 'RESERVATION_DISCOUNT' | 'REGULAR_ORDER' | null;
    return last || 'REGULAR_ORDER';
  });
  const [sortOrder, setSortOrder] = useState<'newest' | 'distance'>('newest');
  const [favoriteStores, setFavoriteStores] = useState<Set<string>>(new Set());
  
  // Ref for map view container
  const mapViewRef = useRef<HTMLDivElement>(null);
  
  // Load user location on auth change
  useEffect(() => {
    if (!authLoading && user) {
      checkAuthAndLoad();
    }
  }, [user, authLoading, checkAuthAndLoad]);

  // Load favorites
  useEffect(() => {
    const loadFavorites = async () => {
      try {
        const result = await favoritesService.getUserFavorites(user?.id || null);
        if (result.success && result.data) {
          setFavoriteStores(new Set(result.data));
        }
      } catch (error) {
        console.error('Failed to load favorites:', error);
      }
    };

    loadFavorites();
  }, [user]);

  // Fetch restaurants
  useEffect(() => {
    const fetchRestaurants = async () => {
      if (authLoading) return;
      
      try {
        setIsLoading(true);
        
        const result = await restaurantService.getRestaurants({ 
          withProducts: true,
          offerType: selectedDiscountType
        });
        
        if (result.success && result.data) {
          const restaurantsWithProducts: RestaurantWithProduct[] = result.data.map(restaurant => ({
            ...restaurant,
            store_type: (restaurant.store_type as 'korean' | 'chinese' | 'japanese' | 'western' | 'cafe' | 'lunchbox' | 'kimbap' | 'salad' | 'bakery' | 'buffet' | 'etc') || 'korean',
            bestmenu: restaurant.bestmenu || '',
            pickup_instructions: restaurant.pickup_instructions || '',
            latitude: restaurant.latitude || 0,
            longitude: restaurant.longitude || 0,
            operating_hours: (restaurant as any).operating_hours,
            todayOperatingHours: (restaurant as any).todayOperatingHours,
            products: restaurant.products?.map(product => ({
              ...product,
              is_available: (product as any).isAvailable ?? (product as any).is_available ?? true
            })) || []
          }));
          setRestaurants(restaurantsWithProducts);
        } else {
          console.error('Failed to fetch restaurants:', result.error);
        }
      } catch (error) {
        console.error('Failed to fetch restaurants:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRestaurants();
  }, [authLoading, selectedDiscountType]);

  // 탭 변경 시 로컬 스토리지에 저장
  useEffect(() => {
    localStorage.setItem('lastHomeOrderTypeDesktop', selectedDiscountType);
  }, [selectedDiscountType]);

  // 마감할인 탭일 때만 제품/오퍼/재고 최소 필드 재조회 (기본 매장 정보는 기존 fetch 유지)
  useEffect(() => {
    let cancelled = false;
    const fetchDeadline = async () => {
      if (selectedDiscountType !== 'DEADLINE_DISCOUNT') {
        setDeadlineProductsByStore(null);
        return;
      }
      const result = await restaurantService.getDeadlineRestaurantsWithOffers();
      if (!cancelled && result.success && result.data) {
        const map = new Map<string, any[]>();
        result.data.forEach((r: any) => {
          if (r.products && r.products.length > 0) {
            map.set(r.id, r.products);
          }
        });
        setDeadlineProductsByStore(map);
      }
    };
    fetchDeadline();
    return () => { cancelled = true; };
  }, [selectedDiscountType]);

  // Filter and sort restaurants
  // 마감할인 탭: 기존 매장 데이터에 마감할인 제품/재고를 주입하고, 재고 없으면 주문불가로 표시
  const restaurantsWithDeadline = useMemo(() => {
    if (selectedDiscountType !== 'DEADLINE_DISCOUNT' || !deadlineProductsByStore) return restaurants;
    return restaurants.map((r) => {
      const products = deadlineProductsByStore.get(r.id) || [];
      const deadlineStockTotal = products.reduce((sum, p: any) => sum + (p.stock?.[0]?.current_stock || 0), 0);
      const hasDeadlineProducts = products.length > 0 && deadlineStockTotal > 0;
      return {
        ...r,
        products,
        is_open: hasDeadlineProducts && (r as any).is_open,
        __hasDeadlineProducts: hasDeadlineProducts
      } as RestaurantWithProduct & { __hasDeadlineProducts?: boolean };
    });
  }, [selectedDiscountType, deadlineProductsByStore, restaurants]);

  const filteredAndSortedRestaurants = useStoreFiltering(
    restaurantsWithDeadline,
    selectedDiscountType,
    sortOrder,
    { selectedStoreTypes, showOpenOnly, showClosedOnly },
    currentLocation?.coordinate
  ).map(restaurant => ({
    ...restaurant,
    pickup_instructions: restaurant.pickup_instructions || '픽업 정보 없음',
    operating_hours: restaurant.operating_hours,
    todayOperatingHours: restaurant.todayOperatingHours
  }))
  // 마감할인 상품/재고 있는 매장 우선 정렬
  .sort((a: any, b: any) => {
    const aHas = a.__hasDeadlineProducts ? 1 : 0;
    const bHas = b.__hasDeadlineProducts ? 1 : 0;
    if (aHas === bHas) return 0;
    return bHas - aHas;
  }) as RestaurantWithProduct[];

  // Handlers
  const handleSortToggle = () => {
    const newSortOrder = sortOrder === 'newest' ? 'distance' : 'newest';
    setSortOrder(newSortOrder);
  };

  const handleAddressButtonClick = () => {
    navigate('/address-select');
  };

  const handleStoreCardClick = (storeId: string) => {
    navigate(`/store/${storeId}?orderType=${selectedDiscountType}`);
  };

  const handleFavoriteToggle = async (storeId: string) => {
    try {
      const result = await favoritesService.toggleFavorite(user?.id || null, storeId);
      
      if (result.success) {
        setFavoriteStores(prev => {
          const newFavorites = new Set(prev);
          if (newFavorites.has(storeId)) {
            newFavorites.delete(storeId);
            showToast('즐겨찾기에서 제거되었습니다');
          } else {
            newFavorites.add(storeId);
            showToast('즐겨찾기에 추가되었습니다');
          }
          return newFavorites;
        });
      } else {
        showToast(result.error || '즐겨찾기 처리에 실패했습니다');
      }
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
      showToast('즐겨찾기 처리에 실패했습니다');
    }
  };

  // Custom toggle handler
  const handleViewToggle = (newIsMapView: boolean) => {
    setIsMapView(newIsMapView);
  };

  // Auto-scroll when map view is activated
  useEffect(() => {
    if (isMapView) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        const mapContainer = document.getElementById('desktop-map-container');
        if (mapContainer) {
          // Get the position of the map container
          const rect = mapContainer.getBoundingClientRect();
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          const targetPosition = rect.top + scrollTop - 120; // 100px offset from top for better visibility
          
          window.scrollTo({
            top: targetPosition,
            behavior: 'smooth'
          });
        }
      }, 150); // Slightly longer delay to ensure render
      
      return () => clearTimeout(timer);
    }
  }, [isMapView]);

  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
          <p className="text-gray-600">
            {authLoading ? "로그인 상태 확인 중..." : "매장 정보를 불러오는 중..."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <HomeSEO 
        structuredData={[
          SCHEMA_TEMPLATES.ORGANIZATION,
          SCHEMA_TEMPLATES.WEB_APPLICATION
        ]}
      />
      <DesktopLayout>
        {/* Address Bar - white background */}
        <div className="bg-white px-6 pt-6 pb-4">
          <HomeHeader onAddressClick={handleAddressButtonClick} />
        </div>
      
      {/* Order Type Selector - white background */}
      <div className="bg-white px-6 pb-4">
        <OrderTypeSelector 
          selectedOrderType={selectedDiscountType}
          onOrderTypeChange={setSelectedDiscountType}
          restaurants={restaurants}
        />
      </div>
      
      {/* Banner Carousel - full width, responsive height */}
      <div className="h-[86px] lg:h-[172px]">
        <BannerCarousel />
      </div>
      
      {/* View Toggle - white background with 24px top spacing, no bottom padding */}
      <div className="bg-white px-6 pt-6">
        <ViewToggle 
          isMapView={isMapView} 
          onToggle={handleViewToggle} 
        />
      </div>

      {/* Store Filter - only show in list view with F5F5F5 background */}
      {!isMapView && (
        <div className="bg-[#F5F5F5] px-6 pt-3 pb-4">
          <StoreFilter 
            sortOrder={sortOrder}
            onSortToggle={handleSortToggle}
          />
        </div>
      )}

      {/* Map View or List View */}
      {isMapView ? (
        <div id="desktop-map-container" ref={mapViewRef} style={{ height: 'calc(100vh - 72px - 200px)' }}>
          <DesktopMapView 
            restaurants={filteredAndSortedRestaurants} 
            selectedOrderType={selectedDiscountType}
          />
        </div>
      ) : (
        <div className="bg-[#F5F5F5] px-6 py-6">
          <StoreListWrapper
            restaurants={filteredAndSortedRestaurants}
            selectedOrderType={selectedDiscountType}
            favoriteStores={favoriteStores}
            onStoreCardClick={handleStoreCardClick}
            onFavoriteToggle={handleFavoriteToggle}
          />
        </div>
      )}
      
      {/* Footer */}
      <div className="mt-12 pb-6">
        <Footer />
      </div>
      
      <ToastContainer />
    </DesktopLayout>
    </>
  );
}
